import { Badge } from "@/components/ui/badge";
import TablePagination from "@/components/TablePagination";
import type { FantasyLeaderboardEntry } from "@/types/api.types";

interface FantasyLeaderboardTableProps {
  entries: FantasyLeaderboardEntry[];
  isLoading: boolean;
  currentPage: number;
  pageSize: number;
  totalEntries: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  /** When true, suppress rank medals/numbers (pre-season, all 0 pts) */
  hideRank?: boolean;
}

const RANK_STYLES: Record<number, string> = {
  1: "bg-yellow-400 text-yellow-900",
  2: "bg-gray-300 text-gray-800",
  3: "bg-orange-300 text-orange-900",
};

const RANK_ICONS: Record<number, string> = {
  1: "🥇",
  2: "🥈",
  3: "🥉",
};

export default function FantasyLeaderboardTable({
  entries,
  isLoading,
  currentPage,
  pageSize,
  totalEntries,
  onPageChange,
  onPageSizeChange,
  hideRank,
}: FantasyLeaderboardTableProps) {
  const totalPages = Math.ceil(totalEntries / pageSize) || 1;
  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border">
        {[...Array(10)].map((_, i) => (
          <div
            key={i}
            className="flex items-center gap-4 p-4 border-b animate-pulse"
          >
            <div className="w-8 h-8 bg-gray-200 rounded-full" />
            <div className="w-8 h-8 bg-gray-200 rounded-full" />
            <div className="flex-1 h-4 bg-gray-200 rounded" />
            <div className="w-16 h-4 bg-gray-200 rounded" />
          </div>
        ))}
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="bg-white rounded-xl border p-12 text-center text-gray-400">
        <Trophy className="h-12 w-12 mx-auto mb-3 opacity-30" />
        <p>No teams yet</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border overflow-hidden">
      {/* Table Header */}
      <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide border-b">
        <div className="col-span-1 text-center">Rank</div>
        <div className="col-span-5">Team</div>
        <div className="col-span-2">Formation</div>
        <div className="col-span-2">Owner</div>
        <div className="col-span-2 text-right">Points</div>
      </div>

      {/* Rows */}
      {entries.map((entry) => (
        <div
          key={entry.teamId}
          className={`grid grid-cols-12 gap-4 px-4 py-3 border-b last:border-0 hover:bg-gray-50 transition items-center ${
            !hideRank && entry.rank <= 3
              ? "bg-gradient-to-r from-yellow-50/40 to-transparent"
              : ""
          }`}
        >
          {/* Rank */}
          <div className="col-span-1 flex justify-center">
            {hideRank ? (
              <span className="text-sm font-bold text-gray-300">—</span>
            ) : entry.rank <= 3 ? (
              <span className="text-xl">{RANK_ICONS[entry.rank]}</span>
            ) : (
              <span
                className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                  RANK_STYLES[entry.rank] || "bg-gray-100 text-gray-600"
                }`}
              >
                {entry.rank}
              </span>
            )}
          </div>

          {/* Team */}
          <div className="col-span-5 flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-xs font-bold shrink-0">
              {entry.teamName.charAt(0).toUpperCase()}
            </div>
            <div>
              <p className="font-semibold text-gray-900 text-sm">
                {entry.teamName}
              </p>
            </div>
          </div>

          {/* Formation */}
          <div className="col-span-2">
            <Badge variant="outline" className="text-xs">
              {entry.formation}
            </Badge>
          </div>

          {/* Owner */}
          <div className="col-span-2">
            <div className="flex items-center gap-2">
              {entry.avatar ? (
                <img
                  src={entry.avatar}
                  className="w-6 h-6 rounded-full"
                  alt=""
                />
              ) : (
                <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-xs font-bold text-gray-500">
                  {entry.userName?.charAt(0) || "?"}
                </div>
              )}
              <span className="text-sm text-gray-600 truncate">
                @{entry.username}
              </span>
            </div>
          </div>

          {/* Points */}
          <div className="col-span-2 text-right">
            <span
              className={`text-lg font-black ${
                hideRank
                  ? "text-gray-900"
                  : entry.rank === 1
                    ? "text-yellow-600"
                    : entry.rank <= 3
                      ? "text-orange-600"
                      : "text-gray-900"
              }`}
            >
              {entry.totalPoints}
            </span>
            <span className="text-xs text-gray-400 ml-1">pts</span>
          </div>
        </div>
      ))}

      {/* Pagination */}
      {totalEntries > 0 && (
        <TablePagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalEntries}
          pageSize={pageSize}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          pageSizeOptions={[20, 50, 100]}
        />
      )}
    </div>
  );
}

// Fix missing import
import { Trophy } from "lucide-react";
